import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSubdomain } from '../../contexts/SubdomainContext';
import api from '../../api/api';
import '../../styles/auth/ParentRegistrationPage.css';

const ParentRegistrationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { center: subdomainCenter, loading: subdomainLoading } = useSubdomain();
  
  // Parent form state
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentAddress, setParentAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Center state
  const [centerInfo, setCenterInfo] = useState(null);
  const [registrationSettings, setRegistrationSettings] = useState(null);
  
  // Custom fields state (if any)
  const [customFields, setCustomFields] = useState({});
  
  // Child toggle and form state
  const [addChild, setAddChild] = useState(false);
  const [childName, setChildName] = useState('');
  const [childGrade, setChildGrade] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Load center information from subdomain or location state
  useEffect(() => {
    // If data was passed via location state (from center code entry), use that
    const locationState = location.state || {};
    if (locationState.centerId && locationState.centerName) {
      setCenterInfo({
        id: locationState.centerId,
        name: locationState.centerName,
        code: locationState.centerCode || ''
      });
      setRegistrationSettings(locationState.registrationSettings);
      return;
    }
    
    // Otherwise, use subdomain center
    if (!subdomainLoading && subdomainCenter) {
      setCenterInfo({
        id: subdomainCenter.id || subdomainCenter._id,
        name: subdomainCenter.name,
        code: subdomainCenter.code || ''
      });
      setRegistrationSettings(subdomainCenter.registrationSettings);
    }
  }, [location.state, subdomainCenter, subdomainLoading]);
  
  // Handle custom field change
  const handleCustomFieldChange = (fieldName, value) => {
    setCustomFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Validate parent fields
    if (!parentName.trim()) newErrors.parentName = 'Name is required';
    if (!parentEmail.trim()) {
      newErrors.parentEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(parentEmail)) {
      newErrors.parentEmail = 'Email is invalid';
    }
    
    if (!parentPhone.trim()) newErrors.parentPhone = 'Phone number is required';
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Validate child fields if adding a child
    if (addChild) {
      if (!childName.trim()) newErrors.childName = 'Child name is required';
      if (!childGrade.trim()) newErrors.childGrade = 'Grade is required';
    }
    
    // Validate custom fields if they're required
    if (registrationSettings?.customFields) {
      registrationSettings.customFields.forEach(field => {
        if (field.required && !customFields[field.name]) {
          newErrors[`custom_${field.name}`] = `${field.label} is required`;
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!centerInfo) {
      setErrors({ general: 'Center information is not available. Please try again.' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const registrationData = {
        centerId: centerInfo.id,
        parentName,
        parentEmail,
        parentPhone,
        parentAddress,
        password,
        customFields,
        hasChild: addChild,
        childInfo: addChild ? {
          name: childName,
          grade: childGrade
        } : null
      };
      
      const response = await api.post('/registrations/submit', registrationData);
      
      if (response.data.success) {
        // Navigate to confirmation screen
        navigate('/register/confirmation', {
          state: {
            centerName: centerInfo.name,
            registrationId: response.data.data.id
          }
        });
      } else {
        alert('Registration Failed: ' + (response.data.message || 'Failed to submit registration'));
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        general: error.response?.data?.message || 'An error occurred while submitting your registration'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Render custom field based on its type
  const renderCustomField = (field) => {
    switch (field.type) {
      case 'text':
      default:
        return (
          <input
            type="text"
            className={`form-input ${errors[`custom_${field.name}`] ? 'input-error' : ''}`}
            placeholder={field.label}
            value={customFields[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
          />
        );
    }
  };
  
  // Show loading state if center info is loading
  if (subdomainLoading || !centerInfo) {
    return (
      <div className="parent-registration-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading registration form...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="parent-registration-container">
      <div className="parent-registration-header">
        <h1>Register</h1>
      </div>
      
      <div className="parent-registration-content">
        <div className="center-info">
          Registering for: <span className="center-name">{centerInfo.name}</span>
        </div>
        
        {errors.general && (
          <div className="general-error">
            {errors.general}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-section">
            <h2 className="section-title">Parent Information</h2>
            
            <div className="form-group">
              <label htmlFor="parentName">Full Name *</label>
              <input
                type="text"
                id="parentName"
                className={`form-input ${errors.parentName ? 'input-error' : ''}`}
                placeholder="Enter your full name"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                disabled={loading}
              />
              {errors.parentName && <div className="error-text">{errors.parentName}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="parentEmail">Email *</label>
              <input
                type="email"
                id="parentEmail"
                className={`form-input ${errors.parentEmail ? 'input-error' : ''}`}
                placeholder="Enter your email address"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                disabled={loading}
              />
              {errors.parentEmail && <div className="error-text">{errors.parentEmail}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="parentPhone">Phone Number *</label>
              <input
                type="tel"
                id="parentPhone"
                className={`form-input ${errors.parentPhone ? 'input-error' : ''}`}
                placeholder="Enter your phone number"
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                disabled={loading}
              />
              {errors.parentPhone && <div className="error-text">{errors.parentPhone}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="parentAddress">Address (Optional)</label>
              <textarea
                id="parentAddress"
                className="form-textarea"
                placeholder="Enter your address"
                value={parentAddress}
                onChange={(e) => setParentAddress(e.target.value)}
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                className={`form-input ${errors.password ? 'input-error' : ''}`}
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              {errors.password && <div className="error-text">{errors.password}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                className={`form-input ${errors.confirmPassword ? 'input-error' : ''}`}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
            </div>
            
            {/* Custom fields based on center settings */}
            {registrationSettings?.customFields?.map((field) => (
              <div key={field.name} className="form-group">
                <label htmlFor={`custom_${field.name}`}>
                  {field.label} {field.required ? '*' : '(Optional)'}
                </label>
                {renderCustomField(field)}
                {errors[`custom_${field.name}`] && (
                  <div className="error-text">{errors[`custom_${field.name}`]}</div>
                )}
              </div>
            ))}
          </div>
          
          {/* Child Information Section */}
          <div className="form-section">
            <div className="child-toggle-container">
              <h2 className="section-title">Add a Child?</h2>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={addChild}
                  onChange={() => setAddChild(!addChild)}
                  disabled={loading}
                />
                <span className="slider round"></span>
              </label>
            </div>
            
            {addChild && (
              <>
                <div className="form-group">
                  <label htmlFor="childName">Child's Name *</label>
                  <input
                    type="text"
                    id="childName"
                    className={`form-input ${errors.childName ? 'input-error' : ''}`}
                    placeholder="Enter child's full name"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    disabled={loading}
                  />
                  {errors.childName && <div className="error-text">{errors.childName}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="childGrade">Grade Level *</label>
                  <input
                    type="text"
                    id="childGrade"
                    className={`form-input ${errors.childGrade ? 'input-error' : ''}`}
                    placeholder="Enter grade level"
                    value={childGrade}
                    onChange={(e) => setChildGrade(e.target.value)}
                    disabled={loading}
                  />
                  {errors.childGrade && <div className="error-text">{errors.childGrade}</div>}
                </div>
              </>
            )}
          </div>
          
          <div className="notice-container">
            <p className="notice-text">
              Your registration will be reviewed by the center administrator.
              You will receive an email once your registration is approved.
            </p>
          </div>
          
          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Registration'}
          </button>
          
          <div className="login-link-container">
            <p>Already have an account?</p>
            <Link to="/login" className="login-link">Sign in here</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ParentRegistrationPage;